// AI-01 / AI-02 / AI-03 / AI-04: one contextual assistant inside Hôm nay and Tìm SIM (no separate chat page). Type or speak; the
// assistant turns the words into editable chips; deterministic code searches the stock; with no result it offers stock-derived
// relaxations. Voice: the browser's speech recognition first; otherwise a short recording sent to the server fallback.
import { api, banner, h, ic, newKey, toast, type Json } from "./ui.ts";

export interface Chips { digits: string; mode: "any" | "head" | "tail"; band: string | null; carrier: string | null; pattern: string | null; min_vnd?: number | null; max_vnd?: number | null }

type SR = { lang: string; interimResults: boolean; maxAlternatives: number; start(): void; stop(): void; onresult: ((e: any) => void) | null; onerror: ((e: any) => void) | null; onend: (() => void) | null };
function speechRecognition(): SR | null {
  const C = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
  if (!C) return null;
  const r = new C() as SR; r.lang = "vi-VN"; r.interimResults = false; r.maxAlternatives = 1; return r;
}

/** Records up to ~15 s with MediaRecorder and sends it to the server fallback. */
async function recordAndTranscribe(status: HTMLElement): Promise<string> {
  if (!navigator.mediaDevices?.getUserMedia || typeof (window as any).MediaRecorder === "undefined") throw new Error("Máy này không ghi âm được trong trình duyệt. Hãy gõ chữ.");
  let stream: MediaStream;
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch { throw new Error("Bạn chưa cho phép dùng micro. Bật quyền micro trong cài đặt trình duyệt, hoặc gõ chữ."); }
  const rec = new (window as any).MediaRecorder(stream) as MediaRecorder;
  const parts: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) parts.push(e.data); };
  const done = new Promise<void>((res) => { rec.onstop = () => res(); });
  rec.start();
  status.textContent = "Đang nghe… (tối đa 15 giây, bấm Dừng khi nói xong)";
  const stopBtn = h("button", { class: "btn btn-secondary btn-sm", type: "button", onclick: () => rec.state !== "inactive" && rec.stop() }, "Dừng");
  status.append(" ", stopBtn);
  const t = setTimeout(() => rec.state !== "inactive" && rec.stop(), 15000);
  await done; clearTimeout(t); stream.getTracks().forEach((x) => x.stop());
  const blob = new Blob(parts, { type: rec.mimeType || "audio/webm" });
  status.textContent = "Đang chuyển giọng nói thành chữ…";
  const res = await fetch("/api/v1/ai/transcribe", { method: "POST", credentials: "same-origin", body: blob,
    headers: { "content-type": (rec.mimeType || "audio/webm").split(";")[0]!, "x-b7b-request": "1", "x-b7b-idempotency-key": newKey("stt") } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message ?? "Không chuyển được giọng nói.");
  if (!j.text) throw new Error(j.note ?? "Không nhận được chữ từ giọng nói.");
  return j.text as string;
}

/** The assistant bar. onChips receives validated chips (the caller fills its editable filters and runs the deterministic search). */
export function assistantBar(placeholder: string, onChips: (c: Chips, info: Json) => Promise<void> | void): HTMLElement {
  const text = h("textarea", { class: "input", rows: "2", placeholder, "aria-label": "Nói hoặc gõ nhu cầu" }) as HTMLTextAreaElement;
  const status = h("div", { class: "small muted", "aria-live": "polite" });
  const out = h("div", {});
  const ask = h("button", { class: "btn btn-ai btn-sm", type: "button" }, ic("spark", "ic ic-sm"), "Hỏi trợ lý") as HTMLButtonElement;
  const voice = h("button", { class: "btn btn-secondary btn-sm", type: "button", "aria-label": "Nói để tìm SIM" }, ic("mic", "ic ic-sm"), "Nói") as HTMLButtonElement;
  const run = async () => {
    if (text.value.trim().length < 2) { toast("Gõ hoặc nói nhu cầu trước."); return; }
    out.replaceChildren(); ask.disabled = true; status.textContent = "Trợ lý đang đọc nhu cầu…";
    try {
      const r = await api("POST", "/api/v1/ai/chips", { text: text.value.trim(), idempotency_key: newKey("chips") });
      status.textContent = r.note;
      if (r.clarify) out.append(banner("info", h("p", {}, r.clarify)));
      await onChips(r.chips, r);
    } catch (e) { out.append(banner("error", h("p", {}, (e as Json).message ?? "Trợ lý chưa trả lời được."))); status.textContent = ""; }
    finally { ask.disabled = false; }
  };
  ask.addEventListener("click", () => void run());
  voice.addEventListener("click", async () => {
    out.replaceChildren();
    const sr = speechRecognition();
    if (sr) {
      status.textContent = "Đang nghe… nói nhu cầu bằng tiếng Việt.";
      sr.onresult = (e) => { const t = e.results?.[0]?.[0]?.transcript ?? ""; text.value = t; status.textContent = "Đã nghe xong. Bạn sửa chữ nếu cần rồi bấm Hỏi trợ lý."; };
      sr.onerror = (e) => { status.textContent = e.error === "not-allowed" ? "Bạn chưa cho phép micro. Bật quyền micro hoặc gõ chữ." : "Không nghe rõ. Thử lại hoặc gõ chữ."; };
      sr.onend = () => { if (status.textContent?.startsWith("Đang nghe")) status.textContent = "Đã dừng nghe."; };
      try { sr.start(); } catch { status.textContent = "Trình duyệt không mở được micro. Hãy gõ chữ."; }
      return;
    }
    try { text.value = await recordAndTranscribe(status); status.textContent = "Đã có chữ từ giọng nói. Sửa nếu cần rồi bấm Hỏi trợ lý."; }
    catch (e) { status.textContent = (e as Error).message; }
  });
  return h("section", { class: "card stack assistant" }, h("div", { class: "row" }, h("span", { class: "pill ai" }, ic("spark", "ic ic-sm"), "Trợ lý AI"), h("span", { class: "small muted" }, "Nói/gõ như nói với người")),
    text, h("div", { class: "row wrap" }, ask, voice), status, out);
}

/** AI-04: honest relaxations computed from stock counts. */
export async function relaxBox(chips: Chips, apply: (c: Chips) => void): Promise<HTMLElement> {
  const r = await api("POST", "/api/v1/ai/relax", { chips });
  if (!r.suggestions.length) return banner("info", h("p", {}, "Không có số nào gần với yêu cầu trong kho đang bán. Thử đổi chữ số hoặc khoảng giá."));
  return h("div", { class: "card stack" }, h("strong", {}, "Không có số khớp hết. Trợ lý gợi ý nới:"),
    ...r.suggestions.map((s: Json) => h("button", { class: "btn btn-secondary btn-sm", type: "button", onclick: () => apply(s.chips) }, `${s.change} → ${s.count} số`)),
    h("p", { class: "small muted" }, r.note));
}
